// /src/js/animator.js

/**
 * ScrollRotator
 */

(function initScrollRotator() {
	if (window.__scrollRotatorSingleton) return;
	window.__scrollRotatorSingleton = true;

	const prefersReduced =
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	// All registered elements
	const items = new Map(); // el -> { rot, speed, active }
	let ticking = false;
	let lastY = window.scrollY;
	let dyAccum = 0;

	// Only animate visible elements
	const io = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const el = entry.target;
				const state = items.get(el);
				if (!state) continue;
				state.active = entry.isIntersecting;
				el.style.willChange = entry.isIntersecting ? "transform" : "";
			}
		},
		{ threshold: 0.1 }
	);

	function onScroll() {
		const y = window.scrollY;
		const dy = y - lastY;
		lastY = y;
		if (dy === 0) return;
		dyAccum += dy;

		if (!ticking) {
			ticking = true;
			requestAnimationFrame(apply);
		}
	}

	function apply() {
		ticking = false;
		if (items.size === 0 || dyAccum === 0) return;

		const delta = dyAccum;
		dyAccum = 0;

		items.forEach((state, el) => {
			if (!state.active) return;
			state.rot += delta * state.speed; // deg
			el.style.setProperty("--cog-rot", `${state.rot}deg`);
		});
	}

	// NOTE: allow optional options so counter.js can pass { speed }
	function register(el, { speed: overrideSpeed } = {}) {
		if (prefersReduced) {
			el.style.setProperty("--cog-rot", "0deg");
			return;
		}
		if (items.has(el)) return;

		const speedAttr = parseFloat(el.getAttribute("data-rotate-speed"));
		const speed = Number.isFinite(overrideSpeed)
			? overrideSpeed
			: Number.isFinite(speedAttr)
			? speedAttr
			: 0.5;

		items.set(el, { rot: 0, speed, active: false });
		io.observe(el);
	}

	// Register any matching elements at load
	function boot(root = document) {
		const elements = root.querySelectorAll("[data-rotate-speed]");
		elements.forEach((el) => register(el));
	}

	// Listeners
	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll);

	// Kick it off
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => boot());
	} else {
		boot();
	}

	// Hook for dynamic block insertions (Gutenberg etc.)
	if (window.wp && wp.data && wp.data.subscribe) {
		let lastCount = 0;
		wp.data.subscribe(() => {
			const count = document.querySelectorAll(
				"[data-rotate-speed]"
			).length;
			if (count !== lastCount) {
				lastCount = count;
				boot(document);
			}
		});
	}

	// 🔐 Expose a singleton rotator object for modules
	window.__scrollRotator = {
		register,
		boot,
	};
})();

export function getScrollRotator() {
	return window.__scrollRotator;
}

export function observeInView(
	root = document,
	{ selector, onEnter, threshold = 0.3, once = true } = {}
) {
	if (!root || !selector) return () => {};

	const nodes = root.querySelectorAll(selector);
	if (!nodes.length) return () => {};

	if (!("IntersectionObserver" in window)) {
		// Fallback: just fire onEnter immediately
		if (typeof onEnter === "function") {
			nodes.forEach((n) => onEnter(n));
		}
		return () => {};
	}

	const io = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) continue;
				if (typeof onEnter === "function") {
					onEnter(entry.target);
				}
				if (once) io.unobserve(entry.target);
			}
		},
		{ threshold }
	);

	nodes.forEach((n) => io.observe(n));

	// Allow caller to manually disconnect if they want
	return () => io.disconnect();
}

// Animate number counters inside a section
// Expects elements like: <span class="counter-value" data-target="1234">0</span>
export function animateCountersIn(
	section,
	{ selector = ".counter-value", duration = 1000, easing = "outQuad" } = {}
) {
	if (!section) return;

	const counters = section.querySelectorAll(selector);
	if (!counters.length) return;

	// Tiny easing helper
	const easings = {
		linear: (t) => t,
		outQuad: (t) => t * (2 - t),
	};

	const ease = easings[easing] || easings.outQuad;

	counters.forEach((el) => {
		const target =
			parseFloat(el.getAttribute("data-target")) ||
			parseFloat(el.textContent.replace(/,/g, "")) ||
			0;

		const startValue = parseFloat(el.textContent.replace(/,/g, "")) || 0;

		const startTime = performance.now();

		function tick(now) {
			const elapsed = now - startTime;
			const t = Math.min(1, elapsed / duration);
			const eased = ease(t);
			const value = startValue + (target - startValue) * eased;

			// If you want ints:
			el.textContent = Math.round(value).toLocaleString();

			if (t < 1) {
				requestAnimationFrame(tick);
			}
		}

		requestAnimationFrame(tick);
	});
}
